import { inject, observer } from 'mobx-react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import useMediaQuery from '@mui/material/useMediaQuery'
import PropTypes from 'prop-types'

import { API_URL } from '../../api'
// Avoid the '../../utils' barrel: wrappers.js imports the components barrel,
// creating a circular dependency that leaves withRouterProps undefined during initialization.
import { withRouterProps } from '../../utils/wrappers'
import { numberToDollars } from '../../utils/utils'
import { IconClose, IconExit } from '../../assets/icons'
import SVGWrapper from '../SVGWrapper'
import StatusPill from '../StatusPill'
import AdviceUndoControl from '../AdviceUndoControl'
import { MOBILE_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'

const Bar = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: ${p => (p.isMobile ? '0 16px' : '16px 24px')};
  border: ${p => (p.isMobile ? 'none' : p.theme.topNavBorder)};
  box-shadow: ${p => (p.isMobile ? 'none' : '0 2px 2px 0 rgba(182, 192, 196, 0.15)')};
  background-color: ${p => p.theme.topNavColor || '#ffffff'};
`

const LogoButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
`

const Logo = styled.img`
  max-width: 175px;
  min-width: 100px;
  width: 100%;
  content: url(${p => API_URL + p.theme.logo});
`

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  column-gap: 40px;
`

const Income = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  line-height: 1.1;
  color: ${p => p.theme.lightestGray};
`

const IncomeAmount = styled.div`
  font-size: 15px;

  &::before {
    content: '$';
    color: ${p => p.theme.lightestGray};
  }
`

const IncomeLabel = styled.div`
  font-size: 12px;
`

const ExitButton = styled.button`
  cursor: pointer;
  padding: 0;
  background-color: transparent;
  color: ${p => p.theme.mediumGray};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 600;
  border: none;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
`

// Give both sides equal space so Undo stays centered even when the status and
// close controls have different widths.
const NavSide = styled.div`
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: ${p => (p.end ? 'flex-end' : 'flex-start')};
`

const AdviceTopNav = props => {
  const { store, engineType, onExit, onLogoClick, navigate, navLink, onUndo, className } = props
  const theme = useTheme()
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)

  // modifiedCase is null until the user creates a modified scenario, so fall
  // back to the recommended case rather than rendering a null / off-track
  // state and hiding the income.
  const viewedCase =
    engineType === 'modified' && store.modifiedCase ? store.modifiedCase : store.recommendedCase
  const isOnTrack = !!viewedCase?.isOnTrack

  const handleExit = onExit || (() => navigate(-1))
  const handleLogoClick = onLogoClick || (() => navigate('/home'))

  const status = <StatusPill isOnTrack={isOnTrack} transparent={isMobile} />

  // On mobile, the summary's Undo control appears in the header rather than
  // the footer.
  if (isMobile) {
    return (
      <Bar className={className} isMobile>
        <NavSide>{status}</NavSide>
        {onUndo && (
          <AdviceUndoControl
            onClick={onUndo}
            label='Undo'
            iconColor={theme.mediumGray ?? '#4a606a'}
          />
        )}
        <NavSide end>
          <CloseButton type='button' onClick={handleExit} aria-label='Exit advice'>
            <SVGWrapper svg={IconClose} fill={theme.lightestGray} size='large' cursor='pointer' />
          </CloseButton>
        </NavSide>
      </Bar>
    )
  }

  return (
    <Bar className={className}>
      <LogoButton type='button' onClick={handleLogoClick} aria-label='Go to home'>
        <Logo alt='Logo' theme={theme} />
      </LogoButton>

      <RightGroup>
        {navLink}
        {status}
        {viewedCase && (
          <Income>
            <IncomeAmount>
              {numberToDollars(viewedCase.projectedMonthlyRetirementInc, true)}
            </IncomeAmount>
            <IncomeLabel>Retirement income</IncomeLabel>
          </Income>
        )}
        <ExitButton type='button' onClick={handleExit}>
          Exit <SVGWrapper svg={IconExit} size='large' cursor='pointer' />
        </ExitButton>
      </RightGroup>
    </Bar>
  )
}

AdviceTopNav.propTypes = {
  engineType: PropTypes.oneOf(['recommended', 'modified']),
  /** Defaults to navigating back. */
  onExit: PropTypes.func,
  onLogoClick: PropTypes.func,
  /** Optional desktop-only link, currently used for "Walk me through." */
  navLink: PropTypes.node,
  /** Shows the summary's Undo control in the mobile header. */
  onUndo: PropTypes.func,
  className: PropTypes.string,
}

AdviceTopNav.defaultProps = {
  engineType: 'recommended',
}

export default withRouterProps(inject('store')(observer(AdviceTopNav)))
